import type { BeforeSendFn } from 'posthog-js';
import posthog from 'posthog-js';
import { getOrCreateSessionDistinctId, isPostHogReady } from 'utils/analyticsDistinctId';
import { getPostHogApiHost } from 'utils/posthog';

export { isPostHogReady };

const POSTHOG_API_KEY = import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY;

// Netlify's own RUM beacon, blocked by ad blockers and corporate filters for a large share of
// users. Matched on the host, mirroring the same regex and reasoning in src/sentry.ts: left
// unfiltered this was ~1,922 of ~2,150 captures (89%) across 502 users in a 14-day window,
// burying every real error below it.
const AD_BLOCKED_BEACON_HOST = /ingesteer\.services-prod\.nsvcs\.net/;

const dropAdBlockedBeaconExceptions: BeforeSendFn = event => {
    if (!event || event.event !== '$exception') {
        return event;
    }

    const exceptions = (event.properties.$exception_list ?? []) as { value?: string }[];
    const isAdBlockedBeacon = exceptions.some(exception => AD_BLOCKED_BEACON_HOST.test(exception.value ?? ''));

    return isAdBlockedBeacon ? null : event;
};

export const isAnalyticsConfigured = (): boolean => Boolean(POSTHOG_API_KEY);
const POSTHOG_API_HOST = getPostHogApiHost();
const isProduction = import.meta.env.VITE_APP_ENV === 'prod';
const isDevelopment = import.meta.env.VITE_APP_ENV === 'dev';
const isLocal = import.meta.env.DEV;

// Session replay and click capture run on every production surface, platform.bizly.com
// included. They were previously scoped to the booking alias, which left the planner app
// — the large majority of production traffic — with no replay and no click capture, so
// incidents there could only be reconstructed from API logs after the fact.

if (POSTHOG_API_KEY && !posthog.__loaded) {
    posthog.init(POSTHOG_API_KEY, {
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: !isProduction,
        capture_heatmaps: false,
        capture_performance: false,
        autocapture: isProduction,
        // Planner sessions contain client meeting data, so keep every input masked. This is
        // the SDK default; it is stated explicitly so widening replay to platform.bizly.com
        // cannot silently start recording typed content.
        session_recording: {
            maskAllInputs: true,
        },
        api_host: POSTHOG_API_HOST,
        flags_api_host: POSTHOG_API_HOST,
        asset_host: POSTHOG_API_HOST,
        ui_host: 'https://us.posthog.com',
        // SDK default is 3000ms, too tight for the local dev proxy (Netlify Dev's redirect
        // engine and Vite's proxy both add an uncached hop in front of PostHog, unlike the
        // production edge network), so flags requests routinely abort in dev.
        feature_flag_request_timeout_ms: isProduction ? 3000 : 10000,
        before_send: dropAdBlockedBeaconExceptions,
        bootstrap: {
            distinctID: getOrCreateSessionDistinctId(),
        },
        on_request_error: (error: unknown) => {
            if (error instanceof Error) {
                console.warn('PostHog request failed:', error.message);
            } else {
                console.warn('PostHog request failed with an unknown error:', error);
            }
        },
        loaded: (client: { debug: () => void }) => {
            if (!isProduction && (isDevelopment || isLocal)) {
                client.debug();
            }
        },
    });
}

export { posthog };
